import type {LiteModeKey} from '@helpers/liteMode';
import type {AppMediaPlaybackController} from '@components/appMediaPlaybackController';
import type {TopPeerType, MyTopPeer} from '@appManagers/appUsersManager';
import type {AccountContentSettings, AccountThemes, AutoDownloadSettings, BaseTheme, BotCommand, Dialog, NotifyPeer, PeerNotifySettings, Theme, ThemeSettings, WallPaper} from '@layer';
import type DialogsStorage from '@lib/storages/dialogs';
import type FiltersStorage from '@lib/storages/filters';
import type {AuthState, Modify} from '@types';
import type {ShortcutKey as PasscodeLockShortcutKey} from '@components/sidebarLeft/tabs/passcodeLock/shortcutBuilder';
import {IS_MOBILE} from '@environment/userAgent';
import getTimeFormat from '@helpers/getTimeFormat';
import App from '@config/app';
import {getAccentPresetsForBase} from '@config/themePresets';
import {ColoredBrushType} from '@components/mediaEditor/context';
import {FontKey} from '@components/mediaEditor/types';
import type {BotConnectionReview} from '@appManagers/appBusinessManager';
import type {UnconfirmedAuthorization} from '@appManagers/appAccountManager';
import type {ContactsSortMode} from '@appManagers/utils/users/sortContacts';

// Factory tinted ("Dark") collapses onto the first base-color preset (blue) so the accent picker
// can omit a separate "default" swatch — resetting to factory now reaches the same state the user
// gets by tapping the blue circle.
const TINTED_DEFAULT_PRESET = getAccentPresetsForBase('baseThemeTinted')[0];

const STATE_VERSION = App.version;
const BUILD = App.build;

export type GlobalNotifySettingsKey =
  NotifyPeer.notifyUsers['_'] |
  NotifyPeer.notifyChats['_'] |
  NotifyPeer.notifyBroadcasts['_'];

// ! DEPRECATED
export type Background = {
  type?: 'color' | 'image' | 'default', // ! DEPRECATED
  blur: boolean,
  highlightingColor?: string,
  color?: string,
  slug?: string,        // image slug
  intensity?: number,   // pattern intensity
  id: string | number,  // wallpaper id
};

export type AppThemeSettings = Modify<ThemeSettings, {
  highlightingColor: string
}>;

export type AppTheme = Modify<Theme, {
  name: 'day' | 'night' | 'light' | 'tinted' | 'system',
  settings?: Array<AppThemeSettings>
}>;

export type AutoDownloadPeerTypeSettings = {
  contacts: boolean,
  private: boolean,
  groups: boolean,
  channels: boolean
};

export type StateSettings = {
  messagesTextSize: number,
  distanceUnit: 'kilometers' | 'miles',
  // Only written once the reader flips a story's weather widget; until then the unit follows the
  // locale, so there is nothing to store and no settings row to show (same as tdesktop).
  temperatureUnit?: 'celsius' | 'fahrenheit',
  sendShortcut: 'enter' | 'ctrlEnter',
  animationsEnabled?: boolean, // ! DEPRECATED
  autoDownload: {
    contacts?: boolean, // ! DEPRECATED
    private?: boolean, // ! DEPRECATED
    groups?: boolean, // ! DEPRECATED
    channels?: boolean, // ! DEPRECATED
    photo: AutoDownloadPeerTypeSettings,
    video: AutoDownloadPeerTypeSettings,
    file: AutoDownloadPeerTypeSettings
  },
  autoDownloadNew: AutoDownloadSettings,
  autoPlay?: { // ! DEPRECATED
    gifs: boolean,
    videos: boolean
  },
  stickers: {
    suggest: 'all' | 'installed' | 'none',
    dynamicPackOrder: boolean,
    loop: boolean,
    /**
     * Groups whose own sticker set / emoji pack the user collapsed in the panel, as
     * `chatId` -> the set id that was collapsed. Keyed by set so that a group swapping
     * packs brings the section back.
     */
    hiddenGroupSets: {[chatId: string]: string},
    hiddenGroupEmojiSets: {[chatId: string]: string}
  },
  emoji: {
    suggest: boolean,
    big: boolean
  },
  background?: Background, // ! DEPRECATED
  themes: AppTheme[],
  theme: AppTheme['name'],
  increaseContrast: boolean,
  // Last explicitly-picked theme variant on each side. The burger-menu Dark-Mode toggle uses
  // these so toggling away and back returns to the same variant (e.g. tinted ↔ classic ↔ tinted)
  // instead of always flipping to the legacy night/classic pair. Updated in themeController on
  // settings.theme changes; radios/UI / `switchTheme(name)` direct calls feed it.
  lastThemeNames: {
    dark: Extract<AppTheme['name'], 'night' | 'tinted'>,
    light: Extract<AppTheme['name'], 'day' | 'light'>
  },
  // Empty-column tip cards (@components/chatTips): which tip is showing, and whether the deck is
  // collapsed to the "select a chat" pill. macOS keeps the same collapse in FastSettings.emptyTips.
  chatTips: {
    index: number,
    hidden: boolean
  },
  notifications: {
    sound: boolean,
    push: boolean,
    desktop: boolean,
    sentMessageSound: boolean,
    suggested: boolean,
    volume: number, // [0..1]
    novibrate?: boolean,
    nopreview?: boolean
  },
  nightTheme?: boolean, // ! DEPRECATED
  timeFormat: 'h12' | 'h23',
  liteMode: {[key in LiteModeKey]: boolean},
  savedAsForum: boolean,
  notifyAllAccounts: boolean,
  tabsInSidebar: boolean,
  /** Ids of the settings-search results picked last, most recent first. */
  settingsSearchRecent: string[],
  seenTooltips: {
    storySound: boolean,
    noForwards: boolean,
    sidebarResize: boolean,
    guestBotPrivacy: number // how many times the guest-bot hint has been shown (capped at 2, like iOS)
  },
  playbackParams: ReturnType<AppMediaPlaybackController['getPlaybackParams']>,
  translations: {
    peers: {[peerId: PeerId]: string},
    enabledPeers: {[peerId: PeerId]: boolean},
    enabled: boolean,
    showInMenu: boolean,
    doNotTranslate: TranslatableLanguageISO[]
  },
  chatContextMenuHintWasShown: boolean,
  passcode: {
    enabled: boolean,
    autoLockTimeoutMins: number, // number | null is not working, gets reset after reloading the page
    lockShortcutEnabled: boolean,
    lockShortcut: PasscodeLockShortcutKey[],
    canAttemptAgainOn: number | null
  },
  logsDiffView?: boolean,
  instantView: {
    scale: number
  },
  cacheTTL: number,
  cacheSize: number,
  showArchiveInChatList: boolean,
  mediaEditor: {
    colorByBrush?: Partial<Record<ColoredBrushType, SavedBrushColor>>;
    brushSize?: number;
    brushType?: string;
    textColor?: SavedBrushColor;
    textSize?: number;
    textAlignment?: string;
    textStyle?: string;
    textFont?: FontKey;
  },
  // Name this browser reports to Telegram as `device_model` in initConnection —
  // what other devices see in Active Sessions. Empty = the raw user agent.
  // Renaming only reaches the server on the next initConnection, which
  // networkerFactory forces by dropping `connectionInited`.
  customDeviceModel: string,
  // Persisted device choices for the audio/video stack used by the
  // SettingsCallsPanel ("Speakers and Camera" tab) and the per-call settings
  // popup. Empty string = follow the OS default (no setSinkId / no deviceId
  // constraint). `micVolume` is a 0..2 multiplier applied to the captured
  // input via a GainNode in StreamManager; 1 = unity.
  callDevices: {
    speakerId: string,
    microphoneId: string,
    cameraId: string,
    micVolume: number,
    // The call settings' "Noise suppression" switch: RNNoise on the
    // microphone where the browser can run it (calls/noiseSuppression),
    // otherwise the browser's own `noiseSuppression` constraint. On by
    // default, as the browser's suppression was before the switch existed.
    noiseSuppression: boolean
  },
  // What the composer's recording button captures when the input is empty.
  // 'voice' shows a microphone icon and records OGG/Opus; 'video' shows a
  // videocamera icon and records a round 360x360 video note. Toggled by
  // clicking the button itself (when input is empty), matches the per-client
  // toggle in tdesktop / iOS / Android.
  recordingMediaType: 'voice' | 'video',
  // The order the contacts tab lists them in, kept between visits the way Android and iOS keep it
  // (tdesktop opens its contacts by last seen every time)
  contactsSortMode: ContactsSortMode,
  // My QR-code popup: remembers the user's last picked chat-theme + brightness
  // so reopens land back where they left off. `nightMode` falls back to the
  // global theme's brightness when unset; `selectedThemeId` empty = the
  // DEFAULT_THEME sentinel (i.e. "use the current chat theme").
  qrCode: {
    nightMode?: boolean,
    selectedThemeId: string
  },
};

// (1 - use swatch, 2 - use picker color), (color from swatch), (color from picker)
export type SavedBrushColor = [1 | 2, string, string];

type CacheSomething<T> = {
  value: T,
  timestamp: number
};

export type State = {
  allDialogsLoaded: DialogsStorage['allDialogsLoaded'],
  pinnedOrders: DialogsStorage['pinnedOrders'],
  communityDialogs: {[communityId: string]: Dialog.dialogCommunity},
  joinedCommunityIds: ChatId[] | null,
  botCommands: {[peerId: PeerId]: {[botId: string]: BotCommand[]}},
  // contactsList: UserId[],
  contactsListCachedTime: number,
  updates: Partial<{
    seq: number,
    pts: number,
    date: number
  }>,
  // filters?: FiltersStorage['filters'], // ! DEPRECATED
  filtersArr?: FiltersStorage['filtersArr'],
  // * `messages.dialogFilters.tags_enabled` - undefined until the server has been asked
  filtersTagsEnabled?: boolean,
  maxSeenMsgId: number,
  stateCreatedTime: number,
  recentEmoji: string[],
  recentCustomEmoji: DocId[],
  emojiVariants: {[emoji: string]: 0 | 1 | 2 | 3 | 4 | 5},
  topPeersCache: {
    [type in TopPeerType]?: {
      peers: MyTopPeer[],
      cachedTime: number
    }
  },
  recentSearch: PeerId[],
  /** Peers whose chat was left behind — closed outright, or switched away from. Newest first. */
  recentlyClosedChats: PeerId[],
  version: typeof STATE_VERSION,
  build: typeof BUILD,
  authState: AuthState,
  /** Key: `getPinnedMessagesKey(peerId, threadId)` — thread-scoped in a forum topic. */
  hiddenPinnedMessages: {[peerIdOrThreadKey: string]: number},
  hideChatJoinRequests: {[peerId: PeerId]: number},
  botConnectionReviews: BotConnectionReview[],
  // stateId?: number, // ! DEPRECATED
  notifySettings: {[k in GlobalNotifySettingsKey]?: PeerNotifySettings.peerNotifySettings},
  confirmedWebViews: BotId[],
  hiddenSimilarChannels: number[],
  appConfig: MTAppConfig,
  accountThemes: AccountThemes.accountThemes,
  shownUploadSpeedTimestamp?: number,
  birthdayContactsDismissedDayKey?: string,
  dontShowPaidMessageWarningFor: PeerId[],
  ageVerification?: {
    date: string,
    layer: number,
    clientVersion: string,
  },
  accountContentSettings: CacheSomething<AccountContentSettings>,
  unconfirmedAuthorizations: UnconfirmedAuthorization[],
  /** When the session this client runs on was created — see FRESH_AUTHORIZATION_PERIOD */
  currentAuthorizationDate: number,


  // playbackParams?: StateSettings['playbackParams'], // ! MIGRATED TO SETTINGS
  // chatContextMenuHintWasShown?: StateSettings['chatContextMenuHintWasShown'], // ! MIGRATED TO SETTINGS
  // seenTooltips?: StateSettings['seenTooltips'], // ! MIGRATED TO SETTINGS
  // translations?: StateSettings['translations'], // ! MIGRATED TO SETTINGS
  settings?: StateSettings // ! DEPRECATED, BUT DON'T REMOVE BEFORE FULL MIGRATION
};

export type CommonState = {
  settings: StateSettings
};

// const BACKGROUND_DAY_MOBILE: Background = {
//   blur: false,
//   slug: '',
//   color: '#dbddbb,#6ba587,#d5d88d,#88b884',
//   highlightingColor: 'hsla(86.4, 43.846153%, 45.117647%, .4)',
//   intensity: 0,
//   id: '1'
// };

// const BACKGROUND_NIGHT_MOBILE: Background = {
//   blur: false,
//   slug: '',
//   color: '#0f0f0f',
//   highlightingColor: 'hsla(0, 0%, 3.82353%, 0.4)',
//   intensity: 0,
//   id: '-1'
// };

export const DEFAULT_THEME: Theme = {
  _: 'theme',
  access_hash: '',
  id: '',
  settings: [{
    _: 'themeSettings',
    pFlags: {},
    base_theme: {_: 'baseThemeClassic'},
    accent_color: 0x3390ec,
    message_colors: [0x5CA853],
    wallpaper: {
      _: 'wallPaper',
      pFlags: {
        default: true,
        pattern: true
      },
      access_hash: '',
      document: undefined,
      id: '',
      slug: 'pattern',
      settings: {
        _: 'wallPaperSettings',
        pFlags: {},
        intensity: 50,
        background_color: 0xdbddbb,
        second_background_color: 0x6ba587,
        third_background_color: 0xd5d88d,
        fourth_background_color: 0x88b884
      }
    }
  }, {
    _: 'themeSettings',
    pFlags: {},
    base_theme: {_: 'baseThemeNight'},
    accent_color: 0x8774E1,
    message_colors: [0x8774E1],
    wallpaper: {
      _: 'wallPaper',
      pFlags: {
        default: true,
        pattern: true,
        dark: true
      },
      access_hash: '',
      document: undefined,
      id: '',
      slug: 'pattern',
      settings: {
        _: 'wallPaperSettings',
        pFlags: {},
        intensity: -50,
        background_color: 0xfec496,
        second_background_color: 0xdd6cb9,
        third_background_color: 0x962fbf,
        fourth_background_color: 0x4f5bd5
      }
    }
  }, {
    _: 'themeSettings',
    pFlags: {},
    base_theme: {_: 'baseThemeTinted'},
    // accent + wallpaper aligned with iOS Dark Blue ("nightAccent"). See submodules/Telegram-iOS/
    // submodules/TelegramPresentationData/Sources/DefaultDarkTintedPresentationTheme.swift —
    // the home wallpaper is the `.blue` baseColor variant from `colorWallpaper` (line 13-14):
    //   case .blue: return (.variant7, 40, [0x1e3557, 0x182036, 0x1c4352, 0x16263a])
    // accent + bubble gradient come from the blue base-color preset so factory state matches what
    // the accent picker offers as its first swatch (the "default" swatch is hidden on tinted).
    accent_color: TINTED_DEFAULT_PRESET.accent_color,
    message_colors: TINTED_DEFAULT_PRESET.message_colors,
    wallpaper: {
      _: 'wallPaper',
      pFlags: {
        default: true,
        pattern: true,
        dark: true
      },
      access_hash: '',
      document: undefined,
      id: '',
      slug: 'pattern',
      settings: {
        _: 'wallPaperSettings',
        pFlags: {},
        // iOS stores intensity 40 (positive) for these dark wallpapers. tweb's pattern renderer
        // expects the dark-pattern sign convention: dark wallpapers carry negative intensity, abs
        // value used as the pattern overlay opacity. So we flip iOS' 40 to -40.
        intensity: -40,
        background_color: 0x1e3557,
        second_background_color: 0x182036,
        third_background_color: 0x1c4352,
        fourth_background_color: 0x16263a
      }
    }
  }, {
    _: 'themeSettings',
    pFlags: {},
    base_theme: {_: 'baseThemeDay'},
    accent_color: 0x2D7ED5,
    message_colors: [0x2D7ED5],
    wallpaper: {
      _: 'wallPaper',
      pFlags: {
        default: true,
        pattern: true
      },
      access_hash: '',
      document: undefined,
      id: '',
      slug: 'pattern',
      settings: {
        _: 'wallPaperSettings',
        pFlags: {},
        intensity: 50,
        background_color: 0xb1e0fa,
        second_background_color: 0x82b0d8,
        third_background_color: 0xa0d8e8,
        fourth_background_color: 0xe5f0f8
      }
    }
  }],
  slug: '',
  title: '',
  emoticon: '🏠',
  pFlags: {default: true}
};

// Per-base highlighting colors used when nothing has been computed from a wallpaper yet.
// Stored on every settings entry so that switching the active base theme keeps highlights coherent
// (mirrors how iOS persists a per-base TelegramThemeSettings array).
const DEFAULT_HIGHLIGHTING_COLORS: {[base in BaseTheme['_']]?: string} = {
  baseThemeClassic: 'hsla(86.4, 43.846153%, 45.117647%, .4)',
  baseThemeNight: 'hsla(299.142857, 44.166666%, 37.470588%, .4)',
  baseThemeTinted: 'hsla(258.461538, 50%, 65.490196%, .4)',
  baseThemeDay: 'hsla(210, 67.741935%, 50.588235%, .4)'
};

const makeDefaultAppTheme = (name: AppTheme['name']): AppTheme => {
  return {
    ...DEFAULT_THEME,
    name,
    settings: DEFAULT_THEME.settings.map((s) => ({
      ...s,
      highlightingColor: DEFAULT_HIGHLIGHTING_COLORS[s.base_theme._] ?? ''
    }))
  };
};

export const SETTINGS_INIT: StateSettings = {
  messagesTextSize: 16,
  distanceUnit: 'kilometers',
  sendShortcut: 'enter',
  autoDownload: {
    photo: {
      contacts: true,
      private: true,
      groups: true,
      channels: true
    },
    video: {
      contacts: true,
      private: true,
      groups: true,
      channels: true
    },
    file: {
      contacts: true,
      private: true,
      groups: true,
      channels: true
    }
  },
  autoDownloadNew: {
    _: 'autoDownloadSettings',
    file_size_max: 3145728,
    pFlags: {
      video_preload_large: true,
      audio_preload_next: true
    },
    photo_size_max: 1048576,
    video_size_max: 15728640,
    video_upload_maxbitrate: 100,
    small_queue_active_operations_max: 0,
    large_queue_active_operations_max: 0
  },
  stickers: {
    suggest: 'all',
    dynamicPackOrder: true,
    loop: true,
    hiddenGroupSets: {},
    hiddenGroupEmojiSets: {}
  },
  emoji: {
    suggest: true,
    big: true
  },
  themes: [
    makeDefaultAppTheme('day'),
    makeDefaultAppTheme('night'),
    makeDefaultAppTheme('tinted'),
    makeDefaultAppTheme('light')
  ],
  theme: 'system',
  increaseContrast: false,
  lastThemeNames: {
    dark: 'night',
    light: 'day'
  },
  chatTips: {
    index: 0,
    hidden: false
  },
  notifications: {
    sound: false,
    push: true,
    desktop: true,
    sentMessageSound: true,
    suggested: false,
    volume: 0.5
  },
  timeFormat: getTimeFormat(),
  liteMode: {
    all: false,
    animations: false,
    motion: false,
    blur: false,
    chat: false,
    chat_background: false,
    chat_spoilers: false,
    effects: false,
    effects_premiumstickers: false,
    effects_reactions: false,
    effects_emoji: false,
    emoji: false,
    emoji_appear: false,
    emoji_messages: false,
    emoji_panel: false,
    gif: false,
    stickers: false,
    stickers_chat: false,
    stickers_panel: false,
    video: false
  },
  savedAsForum: false,
  notifyAllAccounts: true,
  tabsInSidebar: false,
  settingsSearchRecent: [],
  playbackParams: {
    volume: 1,
    boost: 0,
    muted: false,
    playbackRate: 1,
    playbackRates: {
      voice: 1,
      video: 1,
      audio: 1
    },
    loop: false,
    round: false
  },
  chatContextMenuHintWasShown: false,
  seenTooltips: {
    storySound: false,
    noForwards: false,
    sidebarResize: false,
    guestBotPrivacy: 0
  },
  translations: {
    peers: {},
    enabledPeers: {},
    enabled: true,
    showInMenu: true,
    doNotTranslate: []
  },
  passcode: {
    enabled: false,
    autoLockTimeoutMins: 0,
    lockShortcutEnabled: false,
    lockShortcut: ['Alt'],
    canAttemptAgainOn: null
  },
  instantView: {
    scale: 1
  },
  cacheTTL: 86400 * 7, // 1 week
  cacheSize: 0, // Auto
  showArchiveInChatList: true,
  mediaEditor: {
    colorByBrush: {}
  },
  customDeviceModel: '',
  callDevices: {
    speakerId: '',
    microphoneId: '',
    cameraId: '',
    micVolume: 1,
    noiseSuppression: true
  },
  recordingMediaType: 'voice',
  contactsSortMode: 'online',
  qrCode: {
    selectedThemeId: ''
  }
};

export const STATE_INIT: State = {
  allDialogsLoaded: {},
  pinnedOrders: {},
  communityDialogs: {},
  joinedCommunityIds: null,
  botCommands: {},
  // contactsList: [],
  contactsListCachedTime: 0,
  updates: {},
  filtersArr: [],
  maxSeenMsgId: 0,
  stateCreatedTime: Date.now(),
  recentEmoji: [],
  recentCustomEmoji: [],
  emojiVariants: {},
  topPeersCache: {},
  recentSearch: [],
  recentlyClosedChats: [],
  version: STATE_VERSION,
  build: BUILD,
  authState: {
    _: IS_MOBILE ? 'authStateSignIn' : 'authStateSignQr'
  },
  hiddenPinnedMessages: {},
  hideChatJoinRequests: {},
  botConnectionReviews: [],
  // stateId: nextRandomUint(32),
  notifySettings: {},
  confirmedWebViews: [],
  hiddenSimilarChannels: [],
  appConfig: {} as any,
  accountThemes: {} as any,
  birthdayContactsDismissedDayKey: undefined,
  dontShowPaidMessageWarningFor: [],
  accountContentSettings: {} as any,
  unconfirmedAuthorizations: [],
  currentAuthorizationDate: 0
};

export const COMMON_STATE_INIT: CommonState = {
  settings: SETTINGS_INIT
};
